import { useEffect, useRef, useState } from "react";
import { Typography, Paper, Button, Grid, Box, CircularProgress, Badge } from "@mui/material";
import styles from '../my-data.module.scss';
import { observer } from "mobx-react";
import SectionType from "../section/sectionType";
import AddSectionModal from "./add-section-modal";
import DeleteCategoryModal from "./delete-category-modal";

const Category = observer(({ category, uiStore, language, newlyAdded, categoryRef, onTabClick, onDeleteCategory }) => {

    const [editMode, setEditMode] = useState(false);
    const [show, setShow] = useState(false);
    const [labels, setLabels] = useState([]);
    const [info, setInfo] = useState(null);
    const [sections, setSections] = useState([]);
    const [sectionTypes, setSectionTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const { categoryInformation, save_infos } = uiStore;
    const [updatedInfo, setUpdatedInfo] = useState({});
    const [activeSection, setActiveSection] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);
    const sectionErrors = useRef({});
    const inputRefs = useRef([]);

    const update_info = (info) => {
        const clone = { ...updatedInfo };
        clone[info.label + info.infoLanguage] = info;
        setUpdatedInfo(clone);
    }

    const toggleView = async () => {
        if (show) {
            setShow(false);
        } else {
            setShow(true);
            let data = categoryInformation[category.name];

            if (!data) {
                setLoading(true);
                data = await uiStore.fetchCategoryInfo(category.name);
            }
            updateData(data);
            setLoading(false);
        }
    }

    const submit = async (e) => {
        setLoading(true);
        setSubmitLoading(true);
        const data = await save_infos(Object.values(updatedInfo), category.name);
        updateData(data);
        setEditMode(false);
        setLoading(false);
        setSubmitLoading(false);
    }

    const updateData = async (data) => {
        setSectionTypes(data.sectionTypes);
        setSections(data.sections);
        setEditMode(true);
        setLabels(data.labels);
        setInfo(data?.infos?.filter(i => i.infoLanguage === language.language));
    }

    const handleEditModeCollapse = (id, enable) => {
        setEditMode(enable);
        setActiveSection(id);
        setActiveSection('');
        setShow(true);
        return id
    };

    useEffect(() => {
        toggleView()
        if (show) {
            let data = categoryInformation[category.name];

            if (!data) {
                setLoading(true);
                fetchData();
            }

            async function fetchData() {
                data = await uiStore.fetchCategoryInfo(category.name);
            }

            setInfo([]);
            setInfo(data?.infos.filter(i => i.infoLanguage === language.language));
            setLabels(data?.labels);
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language])

    return (
        <div id={category.name} ref={categoryRef}>
            <Box>
                <Button onClick={toggleView}>
                    <Typography fontWeight='700'>
                        {newlyAdded &&
                            <Badge color="secondary" variant="dot"></Badge>
                        }
                    </Typography>
                </Button>
                <>
                    {!loading &&
                        sectionTypes?.map(sectionType => (
                            <SectionType
                                key={sectionType + category.name}
                                sections={sections?.filter(i => i.name === sectionType)}
                                editMode={editMode}
                                uiStore={uiStore}
                                labels={labels}
                                info={info}
                                loading={!!loading}
                                submit={submit}
                                sectionType={sectionType}
                                update_info={update_info}
                                language={language}
                                category={category}
                                updateData={updateData}
                                onDeleteCategory={onDeleteCategory}
                                activeSection={activeSection}
                                sectionErrors={sectionErrors}
                                inputRefs={inputRefs}
                            />
                        ))
                    }

                    {!sectionTypes?.length &&
                        <Paper sx={{ borderRadius: '16px', padding: '20px 0' }}>
                            <Box className={styles.sectionHeader} sx={{ textAlign: 'center', display: 'block' }}>
                                {loading ?
                                    <Box className={styles.sectionHeader} sx={{ textAlign: 'center', display: 'block' }}>
                                        <CircularProgress />
                                    </Box>
                                    :
                                    'No information added yet'
                                }
                            </Box>
                        </Paper>
                    }
                </>

                {submitLoading &&
                    <Paper sx={{ borderRadius: '16px', padding: '20px 0' }}>
                        <Box className={styles.sectionHeader} sx={{ textAlign: 'center', display: 'block' }}>
                            <Box className={styles.sectionHeader} sx={{ textAlign: 'center', display: 'block' }}>
                                <CircularProgress />
                            </Box>
                        </Box>
                    </Paper>
                }

                <Grid item sm={12} display={{ md: 'inline-flex', sm: 'flex' }} sx={{ justifyContent: 'flex-start', mt: 2 }}>

                    <AddSectionModal
                        sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}
                        uiStore={uiStore}
                        category={category.name} updateData={updateData}
                        handleEditModeCollapse={handleEditModeCollapse}
                        sections={sections}
                    />

                    {!category.mandatory &&
                        <DeleteCategoryModal sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}
                            category={category} uiStore={uiStore} updateData={updateData}
                            feedback={() => setShow(false)} onTabClick={onTabClick}
                            onDeleteCategory={onDeleteCategory} />
                    }
                </Grid>
            </Box>

        </div>
    );
});

export default Category;
